import React, { useMemo } from 'react';
import { Stack, Typography } from '@mui/material';
import {
    DataTable,
    MetricMatrix,
    formatCurrency,
    formatNumber,
    formatTimestamp,
} from './SharedFrames';

const pnlCell = (value) => {
    const number = Number(value);
    const tone = Number.isNaN(number) ? 'default' : number > 0 ? 'success' : number < 0 ? 'error' : 'default';
    return (
        <Typography
            component="span"
            variant="body2"
            sx={(theme) => ({
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
                color:
                    tone === 'success'
                        ? theme.palette.success.main
                        : tone === 'error'
                            ? theme.palette.error.main
                            : theme.palette.text.primary,
            })}
        >
            {formatCurrency(value)}
        </Typography>
    );
};

export const cobraColumns = [
    { key: 'symbol', label: 'Symbol' },
    { key: 'side', label: 'Side' },
    { key: 'quantity', label: 'Qty', align: 'right', monospace: true, render: (v) => formatNumber(v, 0) },
    { key: 'entryPrice', label: 'Entry', align: 'right', monospace: true, render: (v) => formatCurrency(v) },
    { key: 'exitPrice', label: 'Exit', align: 'right', monospace: true, render: (v) => formatCurrency(v) },
    { key: 'pnlGross', label: 'Gross', align: 'right', render: (v) => pnlCell(v) },
    { key: 'tcaCost', label: 'TCA', align: 'right', render: (v) => pnlCell(v == null ? null : -Math.abs(v)) },
    { key: 'pnlNet', label: 'Net', align: 'right', render: (v) => pnlCell(v) },
    { key: 'status', label: 'Status' },
    { key: 'exitReason', label: 'Reason' },
    {
        key: 'entryAt',
        label: 'Session',
        render: (_v, row) => {
            const entry = row.entryAt ? formatTimestamp(row.entryAt) : '—';
            const exit = row.exitAt ? formatTimestamp(row.exitAt) : row.status === 'open' ? 'open' : '—';
            return `${entry} → ${exit}`;
        },
    },
];

export const dhruvaColumns = [
    { key: 'tradingDay', label: 'Day' },
    { key: 'accountId', label: 'Account' },
    { key: 'leg', label: 'Leg', render: (v, row) => `${row.strike ?? ''} ${v || ''}`.trim() || '—' },
    { key: 'quantity', label: 'Qty', align: 'right', monospace: true, render: (v) => formatNumber(v, 0) },
    { key: 'entryPrice', label: 'Sold', align: 'right', monospace: true, render: (v) => formatCurrency(v) },
    { key: 'exitPrice', label: 'Bought back', align: 'right', monospace: true, render: (v) => formatCurrency(v) },
    { key: 'pnlGross', label: 'Gross', align: 'right', render: (v) => pnlCell(v) },
    { key: 'tcaCost', label: 'Charges', align: 'right', render: (v) => pnlCell(v == null ? null : -Math.abs(v)) },
    { key: 'pnlNet', label: 'Net', align: 'right', render: (v) => pnlCell(v) },
    {
        key: 'exitReason',
        label: 'Why it exited',
        render: (v, row) => (
            <span title={row.exitNarrative || ''} style={{ cursor: row.exitNarrative ? 'help' : 'default' }}>
                {v || '—'}
            </span>
        ),
    },
    {
        key: 'entryAt',
        label: 'In / Out',
        render: (_v, row) => {
            const entry = row.entryAt ? formatTimestamp(row.entryAt) : '—';
            const exit = row.exitAt ? formatTimestamp(row.exitAt) : row.status === 'open' ? 'open' : '—';
            return `${entry} → ${exit}`;
        },
    },
];

export const vyasaColumns = [
    { key: 'comboLabel', label: 'Combo' },
    { key: 'symbol', label: 'Symbol' },
    { key: 'strike', label: 'Strike', align: 'right', monospace: true, render: (v) => formatNumber(v, 0) },
    { key: 'side', label: 'Side' },
    { key: 'qty', label: 'Qty', align: 'right', monospace: true },
    { key: 'entryPrice', label: 'Entry', align: 'right', monospace: true, render: (v) => formatCurrency(v) },
    { key: 'exitPrice', label: 'Exit', align: 'right', monospace: true, render: (v) => formatCurrency(v) },
    { key: 'pnlGross', label: 'Gross', align: 'right', render: (v) => pnlCell(v) },
    { key: 'tcaTotal', label: 'TCA', align: 'right', render: (v) => pnlCell(v == null ? null : -Math.abs(v)) },
    { key: 'pnlNet', label: 'Net', align: 'right', render: (v) => pnlCell(v) },
    { key: 'exitReason', label: 'Reason' },
    {
        key: 'entryTs',
        label: 'In / Out',
        render: (_v, row) => {
            const entry = row.entryTs ? formatTimestamp(row.entryTs) : '—';
            const exit = row.exitTs ? formatTimestamp(row.exitTs) : '—';
            return `${entry} → ${exit}`;
        },
    },
];

const HistoricalTradesTable = ({
    strategyId,
    from,
    to,
    trades = [],
    totals = {},
    loading = false,
    error = null,
}) => {
    const isVyasa = strategyId === 'vyasa';

    const rows = useMemo(() => {
        if (!isVyasa) return trades;
        return trades.map((t) => ({
            ...t,
            pnlGross: t.pnlGross ?? t.pnl_gross,
            pnlNet: t.pnlNet ?? t.pnl_net,
            tcaTotal: t.tcaTotal ?? t.tca_total,
        }));
    }, [trades, isVyasa]);

    const winRate = useMemo(() => {
        if (totals.winCount != null && totals.closedCount) {
            return ((totals.winCount / totals.closedCount) * 100).toFixed(1);
        }
        const closed = rows.filter((t) => String(t.status || 'closed').toLowerCase() === 'closed');
        if (!closed.length) return null;
        const wins = closed.filter((t) => (t.pnlNet ?? 0) > 0).length;
        return ((wins / closed.length) * 100).toFixed(1);
    }, [rows, totals]);

    const tableDescription = useMemo(() => {
        const shown = rows.length;
        const total = totals.closedCount ?? shown;
        const truncated = total > shown
            ? ` Showing ${shown} most recent of ${total} closed.`
            : '';
        return `${shown} position${shown === 1 ? '' : 's'} between ${from} and ${to} (IST sessions from 05:00).${truncated}${loading ? ' Loading…' : ''}`;
    }, [rows.length, totals.closedCount, from, to, loading]);

    return (
        <Stack spacing={2}>
            <MetricMatrix
                columns={{ xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)', md: 'repeat(5, 1fr)' }}
                metrics={[
                    { label: 'Closed', value: formatNumber(totals.closedCount, 0) },
                    { label: 'Gross', value: formatCurrency(totals.pnlGross), tone: (totals.pnlGross ?? 0) >= 0 ? 'success' : 'error' },
                    { label: 'TCA', value: formatCurrency(totals.tcaCost), tone: 'warning' },
                    { label: 'Net', value: formatCurrency(totals.pnlNet), tone: (totals.pnlNet ?? 0) >= 0 ? 'success' : 'error' },
                    { label: 'Win rate', value: winRate ? `${winRate}%` : '—' },
                ]}
            />

            {error ? (
                <Typography variant="body2" color="error">
                    Could not load historical trades: {error}
                </Typography>
            ) : null}

            <DataTable
                title="Trades"
                description={tableDescription}
                rows={rows}
                columns={strategyId === 'dhruva' ? dhruvaColumns : isVyasa ? vyasaColumns : cobraColumns}
                emptyMessage={loading ? 'Loading trades…' : 'No trades in this date range.'}
                accent="strategy"
                framed={false}
            />
        </Stack>
    );
};

export default HistoricalTradesTable;
